import { useEffect, useState } from "react";
import { X, ChevronDown, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_CHOICES = [
  { value: "submitted", label: "Received" },
  { value: "under_review", label: "Under review" },
  { value: "approved", label: "Approved" },
  { value: "paid", label: "Paid" },
  { value: "rejected", label: "Rejected" },
  { value: "duplicate", label: "Duplicate" },
];

const STATUS_PILL = {
  submitted: { bg: "bg-sky-100", fg: "text-sky-700", label: "Received" },
  under_review: { bg: "bg-amber-100", fg: "text-amber-700", label: "Under review" },
  approved: { bg: "bg-emerald-100", fg: "text-emerald-700", label: "Approved" },
  rejected: { bg: "bg-rose-100", fg: "text-rose-700", label: "Rejected" },
  paid: { bg: "bg-violet-100", fg: "text-violet-700", label: "Paid" },
  duplicate: { bg: "bg-purple-100", fg: "text-purple-700", label: "Duplicate" },
};

function StatusPill({ value, dim = false, testId }) {
  const cfg = STATUS_PILL[value] || {
    bg: "bg-slate-100",
    fg: "text-slate-500",
    label: value || "—",
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center px-2.5 h-7 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.fg} ${
        dim ? "opacity-60" : ""
      }`}
    >
      {cfg.label}
    </span>
  );
}

export default function CustomStatusModal({ submission, onClose, onSaved }) {
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!submission) return null;

  const submit = async () => {
    if (!status) {
      toast.error("Please choose a status");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/submissions/${submission.id}/status`, {
        status,
        admin_notes: message.trim(),
      });
      toast.success(`Status updated to ${status.replace("_", " ")}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="custom-status-modal-backdrop"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ duration: 0.18 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg bg-white rounded-3xl shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="custom-status-title"
          data-testid="custom-status-modal"
        >
          {/* Header */}
          <div className="flex items-start justify-between px-7 pt-7 pb-2">
            <h2
              id="custom-status-title"
              className="text-2xl font-bold text-slate-900 tracking-tight"
            >
              Update Status
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="custom-status-close"
              className="w-9 h-9 rounded-full border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-300 inline-flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-7 pb-6 space-y-6">
            {/* Reference number */}
            <div>
              <div className="text-sm text-slate-500 font-medium mb-1.5">
                Reference Number
              </div>
              <div
                className="text-2xl font-bold text-slate-900 font-mono tracking-tight"
                data-testid="custom-status-reference"
              >
                {submission.reference}
              </div>
            </div>

            {/* Status preview chip: current → new */}
            <div
              className="flex items-center gap-3 flex-wrap"
              data-testid="custom-status-preview"
            >
              <div>
                <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-slate-400 mb-1.5">
                  Current
                </div>
                <StatusPill
                  value={submission.status}
                  testId="custom-status-current-pill"
                />
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 mt-5" />
              <div>
                <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-slate-400 mb-1.5">
                  New
                </div>
                {status ? (
                  <StatusPill
                    value={status}
                    testId="custom-status-new-pill"
                  />
                ) : (
                  <span
                    className="inline-flex items-center px-2.5 h-7 rounded-full text-xs font-semibold bg-slate-100 text-slate-400 border border-dashed border-slate-300"
                    data-testid="custom-status-new-pill-placeholder"
                  >
                    Choose below
                  </span>
                )}
              </div>
            </div>

            {/* New status */}
            <div>
              <label
                htmlFor="custom-status-select"
                className="block text-sm font-semibold text-slate-900 mb-2"
              >
                New Status
              </label>
              <div className="relative">
                <select
                  id="custom-status-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  data-testid="custom-status-select"
                  className={`appearance-none w-full h-14 px-4 pr-12 rounded-2xl border border-slate-300 bg-white text-base font-medium focus:outline-none focus:ring-2 focus:ring-fuchsia-400 focus:border-fuchsia-400 transition-all ${
                    status ? "text-slate-900" : "text-slate-400"
                  }`}
                >
                  <option value="" disabled>
                    Select Status
                  </option>
                  {STATUS_CHOICES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-5 h-5 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            {/* Customer message */}
            <div>
              <label
                htmlFor="custom-status-message"
                className="block text-sm font-semibold text-slate-900 mb-2"
              >
                Customer Message{" "}
                <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <textarea
                id="custom-status-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder="Optional message to send to customer..."
                data-testid="custom-status-message"
                className="w-full px-4 py-3 rounded-2xl border border-slate-300 bg-white text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-fuchsia-400 focus:border-fuchsia-400 resize-none transition-all"
              />
              <p className="mt-2 text-xs text-slate-500">
                This note is included in the customer&apos;s status update email.
              </p>
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex items-center gap-3 px-7 pb-7">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              data-testid="custom-status-cancel"
              className="flex-1 h-14 rounded-2xl border border-slate-300 bg-white text-base font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving || !status}
              data-testid="custom-status-submit"
              className="flex-1 h-14 rounded-2xl text-base font-semibold text-white shadow-lg shadow-fuchsia-500/20 bg-gradient-to-r from-pink-400 to-fuchsia-500 hover:from-pink-500 hover:to-fuchsia-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? "Updating…" : "Update Status"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
